import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import {
  login as apiLogin,
  logout as apiLogout,
  getCurrentUser,
  getAccessToken,
  type LoginResponse,
} from "./api";

// ════════════════════════════════════════════════════════════════════════════
// Auth Context
// ════════════════════════════════════════════════════════════════════════════

export interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
  isPlatformUser?: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, tenantSlug?: string) => Promise<User>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      getCurrentUser()
        .then((userData) => {
          setUser(userData);
        })
        .catch(() => {
          setUser(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string, tenantSlug?: string): Promise<User> => {
    const response: LoginResponse = await apiLogin(email, password, tenantSlug);
    setUser(response.user);
    return response.user;
  };

  const logout = (): void => {
    apiLogout();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// ════════════════════════════════════════════════════════════════════════════
// Protected Route Component
// ════════════════════════════════════════════════════════════════════════════

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
  requirePlatformAdmin?: boolean;
}

export function ProtectedRoute({
  children,
  requiredRoles,
  requirePlatformAdmin,
}: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = "/login";
    return null;
  }

  // Check platform admin requirement
  if (requirePlatformAdmin && (!user?.isPlatformUser || user?.role !== "super_admin")) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-red-600">
          Access denied. Platform admin access required.
        </div>
      </div>
    );
  }

  if (requiredRoles && user && !requiredRoles.includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-red-600">
          Access denied. You don't have permission to view this page.
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
